import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, CircleAlert, MapPin } from 'lucide-react';
import { Button } from '@hqo/ui-atoms';
import { useBookingQuery } from 'app/data/queries/use-booking-query';
import { useCancelBookingMutation } from 'app/data/mutations/use-cancel-booking-mutation';
import { useBookAgain } from 'app/shared/hooks/use-book-again';
import { useActivityDeeplink } from 'app/shared/hooks/use-activity-deeplink';
import { useEventDeeplink } from 'app/shared/hooks/use-event-deeplink';
import { useBuilding } from 'app/shared/contexts/building';
import { useSdkHostInfo } from 'app/shared/contexts/host-info';
import { CTA_TYPES, isDesktopHost, OPEN_MODES } from 'app/shared/consts';
import { ActivityIdentity, toDestinationRoute, useActivityTracking } from 'app/shared/hooks/use-activity-tracking';
import { usePageImpression } from 'app/shared/hooks/use-page-impression';
import {
  BOOKING_STATUS_META,
  bookingStatusView,
  BookingStatusView,
  EVENT_STATUS_META,
  eventStatusView,
  EventStatusView,
} from 'app/shared/consts/booking';
import { getBookingCancelable } from 'app/shared/utils/booking-cancelable';
import { formatBookingDate, formatBookingTimeRange } from 'app/shared/utils/format-booking';
import { useNativeHistoryHeader } from 'app/shared/hooks/use-native-history-header';
import { Spinner } from 'app/components/ui/spinner';
import { EmptyStateShell } from 'app/components/activity-empty-state';
import {
  BookingAmenities,
  BookingCancelModal,
  BookingDetailHeader,
  BookingDetailRow,
} from 'app/components/booking-detail';

/**
 * In-app detail / receipt with a quick cancel action. Opened by tapping a transaction-backed activity
 * TILE (see `activity-feed`) — serves BOTH resource bookings and events (services_booking), which share
 * the transactions endpoint + cancel flow and differ only in their `details` block. Header: desktop →
 * in-app "< Your activity" back button; mobile → native history header (`useNativeHistoryHeader`). Both
 * go back through history so the feed's tabs are preserved. Content is capped at 640px (content-miniapp
 * desktop width) and centered.
 */
export const BookingDetailPage = (): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { transactionUuid = '' } = useParams();
  const { booking, isLoading, isError } = useBookingQuery(transactionUuid);
  const { building } = useBuilding();
  const { hostPlatform } = useSdkHostInfo();
  const desktop = isDesktopHost(hostPlatform);
  const cancelMutation = useCancelBookingMutation();
  const { reset: resetCancelMutation } = cancelMutation;
  // React Router keeps this element mounted across `:transactionUuid` changes, so the cancel mutation
  // state (isSuccess → "cancelled" in-place) would bleed onto a different booking. Reset it per record.
  useEffect(() => {
    resetCancelMutation();
  }, [transactionUuid, resetCancelMutation]);
  const { canOpenResource, openResource } = useBookAgain();
  const tracking = useActivityTracking();
  const { getModuleUrl } = useActivityDeeplink();
  const [isCancelStep, setIsCancelStep] = useState(false);

  useNativeHistoryHeader(!desktop);

  // An event carries a `service` block instead of `resource`/`location_name`.
  const isEvent = !!booking?.details.service;
  const serviceId = booking?.details.service_id;
  const providerName = booking?.details.service?.service_provider?.service_provider_name ?? undefined;
  const resourceId = booking?.details.resource_id ?? booking?.details.resource?.id;

  // Events pre-resolve their "View {event}" url (numeric service_id → provider/service uuids). The CTA
  // shows only once it resolves, so an unresolvable event never dead-taps. The provider name lets the
  // resolver target that one provider instead of scanning them all.
  const { url: eventUrl, open: openEvent } = useEventDeeplink(isEvent ? serviceId : undefined, providerName);

  // On the receipt the record IS the transaction, so its uuid is the item identity for tracking.
  const identity: ActivityIdentity = useMemo(
    () => ({ activityType: isEvent ? 'event' : 'resource_booking', itemUuid: transactionUuid }),
    [isEvent, transactionUuid],
  );

  usePageImpression({
    enabled: !!booking,
    slugs: { transaction_uuid: transactionUuid, activity_type: identity.activityType },
  });

  const onView = useCallback(() => {
    tracking.trackDetailsCta({
      ...identity,
      ctaType: isEvent ? CTA_TYPES.VIEW_EVENT : CTA_TYPES.VIEW_RESOURCE,
      // Events pre-resolve their exact url. A resource's is resolved asynchronously inside
      // `openResource`, so report the module it lands in rather than nothing at all.
      destinationRoute: isEvent
        ? toDestinationRoute(eventUrl ?? null)
        : toDestinationRoute(getModuleUrl('resource_booking')),
    });
    if (isEvent) {
      openEvent();
    } else {
      openResource(resourceId).catch(() => undefined);
    }
  }, [isEvent, openEvent, openResource, resourceId, tracking, identity, eventUrl, getModuleUrl]);

  const goBack = useCallback(() => navigate(-1), [navigate]);
  const openCancelStep = useCallback(() => {
    tracking.trackDetailsCta({ ...identity, ctaType: CTA_TYPES.CANCEL, openMode: OPEN_MODES.MODAL });
    setIsCancelStep(true);
  }, [tracking, identity]);
  const closeCancelStep = useCallback(() => setIsCancelStep(false), []);
  const onConfirmCancel = useCallback(() => {
    if (booking) {
      tracking.trackCancelSubmit(identity);
      cancelMutation.mutate(booking.id, { onSuccess: () => setIsCancelStep(false) });
    }
  }, [booking, cancelMutation, tracking, identity]);

  const header = desktop ? <BookingDetailHeader onBack={goBack} /> : null;

  if (isLoading) {
    return (
      <div data-testid="booking-detail">
        {header}
        <div className="flex justify-center py-16" data-testid="booking-detail-loading">
          <Spinner />
        </div>
      </div>
    );
  }

  if (isError || !booking) {
    return (
      <div data-testid="booking-detail">
        {header}
        <EmptyStateShell
          icon={CircleAlert}
          iconClassName="text-system-pink"
          title={intl.formatMessage({ id: 'booking.detail.error.title' })}
          body={intl.formatMessage({ id: 'booking.detail.error.body' })}
          dataTestId="booking-detail-error"
        />
      </div>
    );
  }

  const { details, content } = booking;
  const timeZone = details.resource?.timezone || building?.timezone;
  // `details` is frozen at booking time and never translated; `content.resource.name` is the only
  // localized copy of the name. Prefer it, and keep the snapshot as the fallback for a booking whose
  // resource has no translation in the reader's language.
  const resourceName = content?.resource?.name || details.name || details.resource?.name || '';
  // Title: event → the service name; booking → the reservation/room name.
  const recordName = isEvent ? details.service?.name || details.name || '' : resourceName;

  // A successful cancel flips the view to cancelled in-place (no refetch needed for the header state).
  const activeView = isEvent ? eventStatusView(details.status) : bookingStatusView(details.status);
  const view = cancelMutation.isSuccess ? 'cancelled' : activeView;
  const statusMeta = isEvent
    ? EVENT_STATUS_META[view as EventStatusView]
    : BOOKING_STATUS_META[view as BookingStatusView];
  const StatusIcon = statusMeta.icon;

  const dateLabel = formatBookingDate(details.start_at, timeZone, intl.locale);
  const timeLabel = formatBookingTimeRange(details.start_at, details.end_at, timeZone, intl.locale);
  const whenLabel = [dateLabel, timeLabel].filter(Boolean).join(' · ');

  const { cancelable, reason } = getBookingCancelable(details);
  const showCancelAction = view !== 'cancelled' && cancelable;
  const notCancelableNote = view !== 'cancelled' && !cancelable && reason
    ? intl.formatMessage({ id: `booking.detail.notCancelable.${reason}` })
    : undefined;

  // Location: event → venue (service.location) + room (service.resource_name); booking → building + room.
  const locationTitle = isEvent
    ? details.service?.location?.name
      ?? details.service?.resource_name
      ?? details.service?.service_provider?.service_provider_name
      ?? undefined
    : details.location_name;
  // Only show the room as a subtitle for an event when the venue name occupies the title (else the
  // room becomes the title and a subtitle would duplicate it).
  const eventLocationSubtitle = details.service?.location?.name ? details.service?.resource_name : undefined;
  const locationSubtitle = isEvent ? eventLocationSubtitle : content?.resource?.name || details.resource?.name;

  // Amenities are resource-booking only (a room's features); events have none.
  const features = isEvent ? [] : (details.resource?.features ?? []);

  const canView = isEvent ? !!eventUrl : canOpenResource(resourceId);
  // Both kinds label the CTA with the record's own name ("View {resource/event name}").
  const viewLabel = intl.formatMessage({ id: 'booking.detail.viewResource' }, { resource: recordName });

  return (
    <div data-testid="booking-detail" className="flex h-full flex-col">
      <BookingCancelModal
        isOpen={isCancelStep}
        resourceName={recordName}
        when={whenLabel}
        cancelable={cancelable}
        isCancelling={cancelMutation.isPending}
        isError={cancelMutation.isError}
        onConfirm={onConfirmCancel}
        onClose={closeCancelStep}
      />
      {/* Static back header (desktop) above the scroll body. */}
      {header && (
        <div className="flex-none pt-4">
          <div className="mx-auto w-full max-w-[640px]">{header}</div>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain">
        <div className={`mx-auto w-full max-w-[640px] px-5 pb-24${desktop ? '' : ' pt-4'}`}>
          <div className="flex items-center gap-3">
            <StatusIcon size={30} strokeWidth={1.75} className={`shrink-0 ${statusMeta.iconClassName}`} aria-hidden />
            <h1 className="text-2xl font-bold tracking-[-0.01em] text-foreground" data-testid="booking-status-title">
              {intl.formatMessage({ id: statusMeta.titleId })}
            </h1>
          </div>
          <p className="mt-3 text-[15px] leading-relaxed text-system-grey-dark">
            {intl.formatMessage({ id: statusMeta.descriptionId }, { resource: recordName, when: dateLabel })}
          </p>

          <h2 className="mt-7 text-lg font-semibold text-foreground">
            {intl.formatMessage({ id: 'booking.detail.details' })}
          </h2>
          <div className="mt-3 flex flex-col gap-5">
            <BookingDetailRow
              icon={Calendar}
              title={dateLabel}
              subtitle={timeLabel}
              actionText={showCancelAction ? intl.formatMessage({ id: 'booking.detail.cancelBooking' }) : undefined}
              onAction={showCancelAction ? openCancelStep : undefined}
              note={notCancelableNote}
              testId="booking-detail-when"
            />
            {locationTitle && (
              <BookingDetailRow
                icon={MapPin}
                title={locationTitle}
                subtitle={locationSubtitle}
                testId="booking-detail-location"
              />
            )}
          </div>

          {features.length > 0 && (
            <div className="mt-7">
              <BookingAmenities features={features} />
            </div>
          )}
        </div>
      </div>

      {canView && (
        <div className="fixed inset-x-0 bottom-0 flex justify-center bg-card px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-[0px_-2px_8px_rgba(45,51,56,0.08)]">
          <Button
            type="button"
            variant="default"
            onClick={onView}
            className="h-14 w-full truncate text-base font-semibold md:w-auto md:min-w-[280px] md:max-w-[420px]"
            data-testid="booking-view-resource"
          >
            {viewLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
